import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { useFeatureFlag } from '../utils/useFeatureFlag';

// Step components (will be created)
import TeamInformationStep from '../components/wizard/TeamInformationStep';
import LineupFormatStep from '../components/wizard/LineupFormatStep';
import TennisLinkImportStep from '../components/wizard/TennisLinkImportStep';
import TennisRecordImportStep from '../components/wizard/TennisRecordImportStep';
import AddMembersStep from '../components/wizard/AddMembersStep';
import WizardCompletion from '../components/wizard/WizardCompletion';

interface TeamFormData {
  name: string;
  league: string;
  section: string;
  district: string;
  flight: string;
  league_start_date: string;
  league_end_date: string;
  timezone: string;
  max_combined_ntrp_rating: number | null;
  match_win_criteria: string | null;
  use_first_match_for_start_date: boolean;
  use_last_match_for_end_date: boolean;
}

interface LineupFormData {
  total_lines: number;
  max_sets_per_line: number;
  allow_multi_line_selection: boolean;
  positions: Array<{ position_number: number; match_type: string }>;
}

const TeamCreationWizard: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isTennisRecordEnabled = useFeatureFlag('tennisrecord-enabled', false);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [subscriptionCapacity, setSubscriptionCapacity] = useState<{
    max_active_teams: number;
    active_teams_count: number;
    remaining_capacity: number;
  } | null>(null);
  const [teamId, setTeamId] = useState<number | null>(null);
  const [teamName, setTeamName] = useState<string>('');
  
  // Helper functions to map between logical steps (displayed) and actual steps (internal)
  // When TennisRecord is disabled, we skip step 4
  const getActualStep = (logicalStep: number): number => {
    if (!isTennisRecordEnabled && logicalStep >= 4) {
      return logicalStep + 1; // Skip step 4 (TennisRecord)
    }
    return logicalStep;
  };
  
  // Get the list of steps to display
  const getDisplaySteps = (): number[] => {
    if (isTennisRecordEnabled) {
      return [1, 2, 3, 4, 5];
    }
    return [1, 2, 3, 4]; // Step 4 is actually step 5 internally
  };
  
  const getStepLabel = (step: number): string => {
    if (!isTennisRecordEnabled) {
      // When TennisRecord is disabled, step 4 is actually Members
      if (step === 4) return 'Members';
    }
    // Normal flow
    if (step === 1) return 'Team Info';
    if (step === 2) return 'Lineup';
    if (step === 3) return 'TennisLink';
    if (step === 4) return 'TennisRecord';
    if (step === 5) return 'Members';
    return '';
  };
  
  // Detect browser timezone for default
  const getBrowserTimezone = (): string => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'America/Los_Angeles';
    }
  };

  const [teamFormData, setTeamFormData] = useState<TeamFormData>({
    name: '',
    league: '',
    section: '',
    district: '',
    flight: '',
    league_start_date: '',
    league_end_date: '',
    timezone: getBrowserTimezone(),
    max_combined_ntrp_rating: null,
    match_win_criteria: null,
    use_first_match_for_start_date: true,
    use_last_match_for_end_date: true,
  });
  
  const [lineupFormData, setLineupFormData] = useState<LineupFormData>({
    total_lines: 5,
    max_sets_per_line: 3,
    allow_multi_line_selection: false,
    positions: [],
  });

  // Fetch subscription capacity on mount
  useEffect(() => {
    const fetchSubscriptionCapacity = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        // Fetch subscription capacity
        const capacityResponse = await axios.get(`/subscriptions/users/${user.id}/subscription/capacity`);
        setSubscriptionCapacity(capacityResponse.data);
      } catch (error: any) {
        console.error('Failed to fetch subscription capacity:', error);
        // If 404, user doesn't have a subscription - set to null explicitly
        if (error.response?.status === 404) {
          setSubscriptionCapacity(null);
        } else {
          toast.error('Failed to load subscription information');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionCapacity();
  }, [user, toast]);

  const handleStep1Next = (data: TeamFormData) => {
    setTeamFormData(data);
    setTeamName(data.name);
    setCurrentStep(2);
  };

  const handleStep2CreateTeam = async (data: LineupFormData) => {
    try {
      setLoading(true);
      
      // Create team (creator will automatically be made a captain by backend)
      const response = await axios.post('/teams/', {
        name: teamFormData.name,
        league: teamFormData.league || null,
        section: teamFormData.section || null,
        district_area: teamFormData.district || null,
        flight_subflight: teamFormData.flight || null,
        league_start_date: teamFormData.league_start_date ? `${teamFormData.league_start_date}T00:00:00` : null,
        league_end_date: teamFormData.league_end_date ? `${teamFormData.league_end_date}T00:00:00` : null,
        use_first_match_for_start_date: teamFormData.use_first_match_for_start_date,
        use_last_match_for_end_date: teamFormData.use_last_match_for_end_date,
        timezone: teamFormData.timezone,
        max_combined_ntrp_rating: teamFormData.max_combined_ntrp_rating || null,
        match_win_criteria: teamFormData.match_win_criteria || null,
        lineup_format: {
          total_lines: data.total_lines,
          max_sets_per_line: data.max_sets_per_line,
          allow_multi_line_selection: data.allow_multi_line_selection,
          positions: data.positions,
        },
      });

      setTeamId(response.data.id);
      setLineupFormData(data);
      
      setCurrentStep(3);
      toast.success('Team created successfully!');
    } catch (error: any) {
      console.error('Failed to create team:', error);
      let errorMessage = 'Failed to create team. Please try again.';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle validation errors (array of error objects)
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Back = () => {
    setCurrentStep(1);
  };

  const handleStep3Continue = () => {
    if (isTennisRecordEnabled) {
      setCurrentStep(4);
    } else {
      // Skip TennisRecord step, go directly to Members (step 5)
      setCurrentStep(5);
    }
  };

  const handleStep4Back = () => {
    setCurrentStep(3);
  };

  const handleStep4Continue = () => {
    setCurrentStep(5);
  };

  const handleStep5Back = () => {
    if (isTennisRecordEnabled) {
      setCurrentStep(4);
    } else {
      // Skip TennisRecord step, go back to TennisLink (step 3)
      setCurrentStep(3);
    }
  };

  const handleStep5Complete = () => {
    setCurrentStep(6);
  };

  const handleCompletionNavigate = (path: string) => {
    navigate(path);
  };

  if (loading && currentStep === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // Render completion step
  if (currentStep === 6) {
    return (
      <WizardCompletion
        teamId={teamId!}
        teamName={teamName}
        onNavigate={handleCompletionNavigate}
      />
    );
  }

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to exit? Any unsaved progress will be lost.')) {
      navigate('/teams');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-green-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">Create New Team</h1>
              <p className="text-gray-600 dark:text-gray-400">Follow the steps below to set up your team</p>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Cancel and exit wizard"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {/* Progress Indicator */}
          <div className="mt-6">
            {/* Step circles and connecting lines - all centered vertically */}
            <div className="flex items-center mb-2">
              {getDisplaySteps().map((step, index) => {
                const actualStep = getActualStep(step);
                const isCompleted = currentStep > actualStep;
                const isCurrent = currentStep === actualStep;
                return (
                  <React.Fragment key={step}>
                    <div
                      className={`flex items-center justify-center w-10 h-10 rounded-full border-2 relative z-10 ${
                        isCurrent || isCompleted
                          ? 'bg-green-600 border-green-600 text-white'
                          : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {isCompleted ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        step
                      )}
                    </div>
                    {index < getDisplaySteps().length - 1 && (
                      <div className="flex-1 h-1 mx-2 relative z-0">
                        <div
                          className={`h-full ${
                            isCompleted ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {/* Step labels - aligned with circles above */}
            <div className="flex">
              {getDisplaySteps().map((step, index) => {
                const actualStep = getActualStep(step);
                const isCurrent = currentStep === actualStep;
                return (
                  <React.Fragment key={step}>
                    <div className="flex justify-center" style={{ width: '40px' }}>
                      <span
                        className={`text-xs whitespace-nowrap ${
                          isCurrent || currentStep > actualStep ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {getStepLabel(step)}
                      </span>
                    </div>
                    {index < getDisplaySteps().length - 1 && <div className="flex-1 mx-2" />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
          {currentStep === 1 && (
            <TeamInformationStep
              initialData={teamFormData}
              subscriptionCapacity={subscriptionCapacity}
              onNext={handleStep1Next}
            />
          )}
          
          {currentStep === 2 && (
            <LineupFormatStep
              initialData={lineupFormData}
              onBack={handleStep2Back}
              onCreateTeam={handleStep2CreateTeam}
              loading={loading}
            />
          )}
          
          {currentStep === 3 && teamId && (
            <TennisLinkImportStep
              teamId={teamId}
              onContinue={handleStep3Continue}
            />
          )}
          
          {currentStep === 4 && teamId && (
            <TennisRecordImportStep
              teamId={teamId}
              onBack={handleStep4Back}
              onContinue={handleStep4Continue}
            />
          )}
          
          {currentStep === 5 && teamId && (
            <AddMembersStep
              teamId={teamId}
              onBack={handleStep5Back}
              onComplete={handleStep5Complete}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamCreationWizard;

